import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { unregister } from './registerServiceWorker';
import { MsalProvider, MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType, PublicClientApplication } from "@azure/msal-browser";
import { authConfig } from './authProvider';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack'

const themeOptions = {
    palette: {
        type: 'light',
        primary: {
            main: '#76070D',
        },
        secondary: {
            main: '#000000',
        },
        tertiary: {
            main: '#FFFFFF',
        },
        quaternary: {
            main: '#3B4B57',
        },
        error: {
            main: '#3B4B57',
        },
        success: {
            main: '#3B4B57',
        },
        warning: {
            main: '#D9D9D9',
        },
        info: {
            main: '#fff',
        },
        text: {
            main: '#212121',
            primary: '#303030',
            secondary: '#fff',
            warning: '#000',
        },
        navbar: {
            main: '#fff',
        },
        button: {
            main: '#1E1E1E'
        },
        Purchase: {
            main: "#880000"
        },
        margins: {
            main: "#3C495A"
        }
    },
    typography: {
        body: 'verdana',
        heading: 'rubik',
        button: 'rubik',
        monospace: 'monospace',
    },
    props: {
        MuiTooltip: {
            arrow: true,
        },
    },
    components: {
        MuiTable: {
            styleOverrides: {
                root: {
                    backgroundColor: "#fff",
                    color: "#303030ff"
                },
            },
        },
        MUIDataTableHeadCell: {
            styleOverrides: {
                sortAction: {
                    '& .MuiTableSortLabel-icon': {
                        color: "#303030ff !important"
                    }
                }
            }
        },
        MUIDataTableToolbar: {
            styleOverrides: {
                root: {
                    backgroundColor: "#fff",
                    color: "#303030ff"
                },
            }
        },
        MUIDataTableToolbarSelect: {
            styleOverrides: {
                root: {
                    backgroundColor: "#ebebebff",
                    color: "#303030ff"
                },
            }
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: "rgb(0 0 0 / 65%) !important"
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 9999
                }
            }
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    color: "rgb(0 0 0 / 65%) !important"
                }
            }
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: "#800000 !important"
                }
            }
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    color: "#800000 !important"
                }
            }
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true
            },
            styleOverrides: {
                root: {
                    borderRadius: 9999,
                    fontWeight: 700,
                    textTransform: 'uppercase'
                },
                sizeLarge: {
                    paddingTop: 12,
                    paddingBottom: 12,
                    paddingLeft: 24,
                    paddingRight: 24,
                    fontSize: '1rem'
                },
                containedPrimary: {
                    backgroundColor: '#880000',
                    color: '#ffffff',
                    '&:hover': {
                        backgroundColor: '#660000'
                    }
                },
                outlinedPrimary: {
                    border: '2px solid #880000',
                    color: '#880000',
                    backgroundColor: '#ffffff',
                    '&:hover': {
                        border: '2px solid #660000',
                        color: '#660000',
                        backgroundColor: '#f5f5f5',
                        borderWidth: 2
                    }
                }
            },
            variants: [
                {
                    props: { variant: 'Primary' },
                    style: {
                        whiteSpace: "nowrap",
                        boxShadow: 'none',
                        borderRadius: 9999,
                        borderTopRightRadius: 9999,
                        borderBottomRightRadius: 9999,
                        textTransform: 'none',
                        paddingLeft: 32,
                        paddingRight: 32,
                        paddingTop: 10,
                        paddingBottom: 10,
                        fontSize: 16,
                        backgroundColor: '#880000',
                        color: '#ffffff',
                        '&:hover': {
                            backgroundColor: '#660000'
                        }
                    }
                },
                {
                    props: { variant: 'Secondary' },
                    style: {
                        whiteSpace: "nowrap",
                        textTransform: 'none',
                        borderRadius: 9999,
                        paddingLeft: 32,
                        paddingRight: 32,
                        paddingTop: 10,
                        paddingBottom: 10,
                        fontSize: 16,
                        color: '#1E1E1E',
                        backgroundColor: 'transparent',
                        '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.04)'
                        }
                    }
                }
            ]
        },
        MuiButtonGroup: {
            variants: [
                {
                    props: { variant: 'comboButton' },
                    style: {
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#ffffff',
                        borderRadius: 9999,
                        border: '1px solid rgba(255,255,255,0.9)',
                        padding: 2,
                        columnGap: 8,
                        boxShadow: '0px 3px 5px 1px rgba(0, 0, 0, 0.25)',
                        overflow: 'visible',
                        '& .MuiButtonGroup-grouped': {
                            borderRadius: '9999px !important',
                            borderTopLeftRadius: '9999px !important',
                            borderBottomLeftRadius: '9999px !important',
                            borderTopRightRadius: '9999px !important',
                            borderBottomRightRadius: '9999px !important'
                        },
                        '& .MuiButtonGroup-grouped:not(:last-of-type)': {
                            marginRight: 0
                        },
                        '& .MuiButtonGroup-grouped:not(:first-of-type)': {
                            marginLeft: 0
                        }
                    }
                }
            ]
        }
    }
};

const theme = createTheme(themeOptions);
const baseUrl = document.getElementsByTagName('base')[0].getAttribute('href');
const rootElement = document.getElementById('root');
const root = createRoot(rootElement);
const authRequest = {
    scopes: ["openid", "profile"]
};

const initializePca = async (config) => {
    const pca = await PublicClientApplication.createPublicClientApplication(config);
    return pca;
}

initializePca(authConfig)
    .then(authProvider => {
        root.render(
            <BrowserRouter basename={baseUrl}>
                <MsalProvider instance={authProvider}>
                    <MsalAuthenticationTemplate
                        interactionType={InteractionType.Redirect}
                        authenticationRequest={authRequest}
                    >
                        <ThemeProvider theme={theme}>
                            <SnackbarProvider maxSnack={4} autoHideDuration={4000}>
                                <App />
                            </SnackbarProvider>
                        </ThemeProvider>
                    </MsalAuthenticationTemplate >
                </MsalProvider>
            </BrowserRouter>
        );

        unregister();
    });

